import { useMemo } from "react";
import DOMPurify from "dompurify";
import { marked } from "marked";
import { cn } from "@/lib/cn";
import { HEADING_CLASS, IMAGE_CLASS, MARK_CLASS } from "./richTextClasses";

marked.setOptions({ breaks: true, gfm: true });

interface Props {
  text: string | null | undefined;
  className?: string;
  /** Known @mention names (e.g. roster). Occurrences of "@Name" in the
   *  rendered output get a highlight chip. Names are matched longest-
   *  first so "@Tom Velin" wins over a hypothetical "@Tom". */
  mentionNames?: string[];
  /** Resolves "@token" handles (e.g. Flow's @avery.chen) to a display
   *  label; resolved ones render as a chip. Unresolved stay plain text. */
  resolveMention?: (token: string) => { label: string; title?: string } | undefined;
  /** Maps a stored image src to what the browser loads; null drops the
   *  image (its alt text stays). Without it images load as written. */
  resolveImageSrc?: (src: string) => string | null;
}

/** Point images at their resolved src, or replace them with their alt text. */
function resolveImages(html: string, resolve: NonNullable<Props["resolveImageSrc"]>): string {
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  for (const img of Array.from(tpl.content.querySelectorAll("img"))) {
    const src = resolve(img.getAttribute("src") ?? "");
    if (src) img.setAttribute("src", src);
    else img.replaceWith(img.getAttribute("alt") ?? "");
  }
  return tpl.innerHTML;
}

const CHIP =
  "rounded bg-brand-navy-100 dark:bg-brand-navy-500/15 px-1 py-0.5 text-[0.9em] font-medium text-brand-navy-700 dark:text-brand-navy-300";
const HANDLE = /(^|\s)@([A-Za-z0-9._@-]*[A-Za-z0-9_])/g;

/** Wrap resolvable @handles in text nodes (never inside links or code). */
function chipHandles(html: string, resolve: NonNullable<Props["resolveMention"]>): string {
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  const walker = document.createTreeWalker(tpl.content, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  while (walker.nextNode()) {
    const t = walker.currentNode as Text;
    if (t.data.includes("@") && !t.parentElement?.closest("a, code, pre")) nodes.push(t);
  }
  for (const node of nodes) {
    const frag = document.createDocumentFragment();
    let last = 0;
    for (const m of node.data.matchAll(HANDLE)) {
      const who = resolve(m[2]);
      if (!who) continue;
      const at = m.index! + m[1].length;
      frag.append(node.data.slice(last, at));
      const chip = document.createElement("span");
      chip.className = CHIP;
      chip.textContent = `@${who.label}`;
      if (who.title) chip.title = who.title;
      frag.append(chip);
      last = at + 1 + m[2].length;
    }
    if (last === 0) continue;
    frag.append(node.data.slice(last));
    node.replaceWith(frag);
  }
  return tpl.innerHTML;
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Renders markdown as sanitized HTML. Source value stays plain markdown
 * text so existing data, search, and tool-call payloads keep working —
 * we only convert at the render layer.
 */
export function RichText({ text, className, mentionNames, resolveMention, resolveImageSrc }: Props) {
  const html = useMemo(() => {
    const raw = (text ?? "").trim();
    if (!raw) return "";
    const rendered = marked.parse(raw, { async: false }) as string;
    let out = DOMPurify.sanitize(rendered);
    // Mention highlighting AFTER sanitize: we only inject our own span,
    // and only around text that survived sanitization.
    if (mentionNames?.length) {
      // ONE alternation pass (longest name first) so a short name can
      // never re-match inside a longer name's already-injected span.
      const names = mentionNames
        .map((n) => n.trim())
        .filter(Boolean)
        .sort((a, b) => b.length - a.length);
      if (names.length) {
        const rx = new RegExp(`@(?:${names.map(escapeRegExp).join("|")})`, "g");
        out = out.replace(
          rx,
          (m) =>
            `<span class="rounded bg-brand-navy-100 dark:bg-brand-navy-500/15 px-1 py-0.5 text-[0.9em] font-medium text-brand-navy-700 dark:text-brand-navy-300">${m}</span>`,
        );
      }
    }
    if (resolveMention) out = chipHandles(out, resolveMention);
    if (resolveImageSrc) out = resolveImages(out, resolveImageSrc);
    return out;
  }, [text, mentionNames, resolveMention, resolveImageSrc]);

  if (!html) return null;

  return (
    <div
      className={cn(
        "prose prose-sm max-w-none text-ink-2",
        "prose-p:my-2 prose-p:leading-relaxed",
        "prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5",
        "prose-strong:text-ink prose-strong:font-semibold",
        "prose-a:text-brand-navy-600 prose-a:no-underline hover:prose-a:underline",
        "prose-code:rounded prose-code:bg-raised prose-code:px-1 prose-code:py-0.5 prose-code:text-[0.85em] prose-code:font-mono prose-code:text-ink prose-code:before:content-none prose-code:after:content-none",
        "prose-headings:text-ink prose-headings:font-semibold",
        "prose-blockquote:my-2 prose-blockquote:font-normal prose-blockquote:not-italic prose-blockquote:text-ink-3",
        // Highlighter marks (any color), readable in both themes.
        MARK_CLASS,
        HEADING_CLASS,
        IMAGE_CLASS,
        className,
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
